import some from 'lodash/some';
import * as Sentry from '@sentry/vue';
import { getActiveSubscription } from '@/data/session/session-globals';
import { PENDO_INTERNAL_SUB_ID } from '@/settings/subscription-settings/utils';
import { isProdEnvironment } from '@/settings/environment.utils.js';
import { getSubIdFromUrlPath } from '@/utils/sub-id-routing.utils';

let sentryInitialized = false;

export function isEventFromLocalhost (event = {}) {
    const url = event?.request?.url;

    return typeof url === 'string' && (url.includes('local.pendo.io') || url.includes('localhost'));
}

// Sentry SDK v9+ renamed the global handler mechanism type from 'onerror' to
// 'auto.browser.global_handlers.onerror', so both values must be matched.
const ONERROR_MECHANISM_TYPES = ['onerror', 'auto.browser.global_handlers.onerror'];

export function isEventFromVue (event = {}) {
    return some(
        event?.exception?.values,
        ({ mechanism, type }) => type === 'CustomEvent' && ONERROR_MECHANISM_TYPES.includes(mechanism?.type),
    );
}

export function beforeSendSentryEvent (event, hint) {
    setTimeout(() => console.error(hint?.originalException || hint?.syntheticException || 'Unknown error'), 0);

    if (isEventFromLocalhost(event)) return null;

    // Sentry listens for 'error' events on the window but its handler expects an Error object,
    // not the CustomEvent we wrap Vue component errors in. Sentry reporting is handled when
    // creating the CustomEvent so we need to drop it before sending to prevent double reporting.
    //
    // @see vue-app#applySharedConfigToVueApp
    if (isEventFromVue(event)) return null;

    return event;
}

/**
 * Performance tracing (page load, navigation, Web Vitals) is intentionally limited to the
 * pendo-internal subscription in production so we can monitor Engage UI load times without
 * collecting performance data for every customer subscription.
 */
export function shouldEnablePerformanceTracing () {
    if (!isProdEnvironment()) return false;

    const activeSub = getActiveSubscription();
    const activeSubId = activeSub?.id || getSubIdFromUrlPath();

    return activeSubId === PENDO_INTERNAL_SUB_ID;
}

export function setSampleRate (store) {
    return shouldEnablePerformanceTracing(store) ? 0.1 : 0;
}

export function initSentry (store, router) {
    if (!isProdEnvironment() || sentryInitialized) return;

    const releaseVersion = __PROCESS__.PENDO_RELEASE;

    try {
        Sentry.init({
            release: `engage-ui@${releaseVersion}`,
            // eslint-disable-next-line internal-rules/no-email-addresses
            dsn: 'https://3599373947c84bd49c23de5b7f838908@o69161.ingest.sentry.io/4504399166046208',
            integrations: [
                Sentry.browserTracingIntegration({
                    router,
                    instrumentPageLoad: true,
                    instrumentNavigation: true,
                    // wait 5s after route navigation before closing sentry's current reporting span. Ensures that
                    // we catch aggs that don't get kicked off for a second or two until after route load
                    idleTimeout: 5_000,
                    // max timeout for any individual metric. Especially with long-running aggs, this will give us
                    // better insight into exactly how long they take
                    childSpanTimeout: 90_000,
                    // needs to be longer than childSpanTimeout, to make sure we don't truncate it prematurely
                    finalTimeout: 120_000,
                }),
                Sentry.browserProfilingIntegration(),
            ],
            attachStacktrace: true,
            environment: __PROCESS__.PENDO_ENV,
            sampleRate: 1,
            tracesSampleRate: setSampleRate(store),
            profileSessionSampleRate: setSampleRate(store),
            profileLifecycle: 'trace',
            beforeSend: beforeSendSentryEvent,
        });
        sentryInitialized = true;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
    }
}

export function attachSentryVueApp (app) {
    if (!sentryInitialized || !app) return;

    Sentry.addIntegration(
        Sentry.vueIntegration({
            app,
            attachErrorHandler: false,
        }),
    );
}

/** @internal test helper */
export function resetSentryForTests () {
    sentryInitialized = false;
}
